import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Spinner from "@/v2-ui/spinner"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Placeholder from "@/v2-ui/Placeholder"
import LayerCountDriftBlock from "@/v2-backoffice-client/layer/drift/LayerCountDriftBlock"
import LayerDriftHeader from "@/v2-backoffice-client/layer/drift/LayerDriftHeader"
import LayerDriftTable from "@/v2-backoffice-client/layer/drift/LayerDriftTable"
import { useLayerDriftQuery } from "@/v2-backoffice-client/layer/drift/layer.drift.query"
import { MAP_LAYER_MONITOR_SOURCE } from "@/v2-backoffice-client/layer/layer.constants"

export type LayerDriftTabProps = ComponentProps<"div"> & {
  featureCode: string | null | undefined
  // True while the panel's on-open live probe is in flight, so the empty
  // state can say "checking now" instead of "wait for the next cron run".
  isProbing?: boolean
}

// LayerDriftTab is the top-level orchestrator for the Drift tab in
// LayerViewSheet. It owns the query lifecycle and the empty / error /
// loading branches; everything below the header / table split lives in
// its own focused component so this file stays readable.
function LayerDriftTab(props: LayerDriftTabProps) {
  const { featureCode, isProbing, className, ...divProps } = props

  const { data, loading, error, refetch } = useLayerDriftQuery({
    skip: !featureCode,
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        source: MAP_LAYER_MONITOR_SOURCE,
        identifier: String(featureCode ?? ""),
      },
    },
  })

  const detail = data?.getLayerDriftDetail ?? null

  return (
    <div className={cn("flex flex-col gap-3 p-4", className)} {...divProps}>
      {(loading || isProbing) && !detail ? (
        <Spinner className="m-auto relative" displaySize="sm">
          {isProbing
            ? "Probing the layer's service for metadata changes..."
            : "Checking for layer-server metadata changes..."}
        </Spinner>
      ) : error ? (
        <ErrorMessage error={error} />
      ) : !detail ? (
        <Placeholder className="py-6">
          No fingerprint could be captured for this layer — its configuration
          has no directly probeable service URL (portal-item layers, for
          instance), so drift cannot be tracked.
        </Placeholder>
      ) : (
        <>
          <LayerDriftHeader detail={detail} onAccepted={() => refetch()} />
          {detail.Entries.length === 0 ? (
            <Placeholder className="py-6">
              The current fingerprint matches the accepted baseline exactly.
            </Placeholder>
          ) : (
            <LayerDriftTable entries={detail.Entries} />
          )}
          {detail.CountDrift && (
            <LayerCountDriftBlock
              countDrift={detail.CountDrift}
              onAccepted={() => refetch()}
            />
          )}
        </>
      )}
    </div>
  )
}

export default LayerDriftTab
